import { ActionButton, ActionButtonType } from "@octopusdeploy/design-system-components";
import type { GetProcessTemplateSharingConfigurationResponse, GitRef, ProcessTemplateResource, ProcessTemplateVersionResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { useState } from "react";
import semver from "semver/preload";
import type { ProcessTemplateProducerActionDispatcher } from "~/analytics/Analytics";
import type { TemplateWizardPageProps } from "~/areas/platformHub/TemplateWizardDialogLayout";
import TemplateWizardDialogLayout from "~/areas/platformHub/TemplateWizardDialogLayout";
import type { EditProcessTemplateLookupData, SaveProcessTemplateParams } from "~/areas/platformHub/processTemplates/EditProcessTemplatePage";
import { processTemplateStepsContainAnyUnsupportedActionTypes } from "~/areas/platformHub/processTemplates/EditProcessTemplatePage";
import type { ReleaseType } from "~/areas/platformHub/processTemplates/PublishTemplateDialog";
import { PublishTemplateDialog } from "~/areas/platformHub/processTemplates/PublishTemplateDialog";
import type { SharedSpaces } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import { ShareTemplateDialog, ShareTemplateSpacesType } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import { CommitSummaryAndDetails } from "~/areas/projects/components/VersionControl/CommitDialog";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import CustomDialog from "~/components/Dialog/CustomDialog";
import { PlatformHubTemplateType } from "../platformHubHelpers";
export enum ActionCompleted {
    Committed = "committed",
    Published = "published",
    Shared = "shared"
}
function getShareType(sharedSpaces: SharedSpaces): ShareTemplateSpacesType {
    if (sharedSpaces.shareToAll) {
        return ShareTemplateSpacesType.ShareAll;
    }
    if (sharedSpaces.individualSpacesSharedTo.length > 0) {
        return ShareTemplateSpacesType.ShareIndividual;
    }
    return ShareTemplateSpacesType.NotShared;
}
export enum TemplateWizardSteps {
    "Commit",
    "Publish",
    "Share"
}
export type CommitType = "Commit and Publish" | "Commit Only" | "Quick Commit";
export interface CommitParams {
    commitMessage?: CommitMessageWithDetails;
}
export interface PublishParams {
    versionToPublish: string;
    isPreRelease: boolean;
    releaseType: ReleaseType;
}
export interface ShareParams {
    shareType: ShareTemplateSpacesType;
    selectedSpaces: string[];
}
export interface WizardSaveParams {
    type: "Wizard";
    commitParams?: CommitParams;
    publishParams?: PublishParams;
    shareParams?: ShareParams;
}
interface ProcessTemplateWizardDialogProps {
    open: boolean;
    busy: boolean;
    errors: Error[];
    model: ProcessTemplateResource;
    gitRef: GitRef;
    defaultBranch: string;
    lookupData: EditProcessTemplateLookupData;
    sharingConfiguration: GetProcessTemplateSharingConfigurationResponse | null;
    reloadDataAfterSave: () => void;
    stepsToShow: TemplateWizardSteps[];
    publishedVersions: ProcessTemplateVersionResource[];
    getDefaultCommitMessage: () => string;
    setShowInitialSuccessCallout: (show: boolean) => void;
    closeDialog: () => void;
    dispatchProcessTemplateAction: ProcessTemplateProducerActionDispatcher;
    saveProcessTemplate: (params: SaveProcessTemplateParams) => Promise<void>;
}
export function ProcessTemplateWizardDialog({ busy, errors, gitRef, defaultBranch, model, open, lookupData, sharingConfiguration, stepsToShow, publishedVersions, getDefaultCommitMessage, closeDialog, saveProcessTemplate, }: ProcessTemplateWizardDialogProps) {
    const defaultSharingConfiguration = { shareToAll: sharingConfiguration?.SharedToAllSpaces ?? false, individualSpacesSharedTo: sharingConfiguration?.IndividuallySharedSpaceIds ?? [] };
    const emptyCommitMessage = { summary: "", details: "" };
    const defaultVersion = "1.0.0";
    const [selectedSpaces, setSelectedSpaces] = useState<string[]>(defaultSharingConfiguration.individualSpacesSharedTo);
    const [shareType, setShareType] = useState<ShareTemplateSpacesType>(getShareType(defaultSharingConfiguration));
    const [canSaveShare, setCanSaveShare] = useState<boolean>(false);
    const latestNonPreReleaseVersion = publishedVersions.find((v) => !semver.prerelease(v.Version));
    const [versionToPublish, setVersionToPublish] = useState<string>(latestNonPreReleaseVersion ? (semver.inc(latestNonPreReleaseVersion.Version, "major") ?? defaultVersion) : defaultVersion);
    const [releaseType, setReleaseType] = useState<ReleaseType>("Major");
    const [isPreRelease, setIsPreRelease] = useState<boolean>(false);
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>(emptyCommitMessage);
    const [hasSaveBeenAttempted, setHasSaveBeenAttempted] = useState<boolean>(false);
    const showCommit = stepsToShow.includes(TemplateWizardSteps.Commit);
    const showPublish = stepsToShow.includes(TemplateWizardSteps.Publish);
    const showShare = stepsToShow.includes(TemplateWizardSteps.Share);
    const handleSave = async (isCommitOnly: boolean) => {
        const commitParams: CommitParams | undefined = showCommit
            ? {
                commitMessage: showCommit ? commitMessage : undefined,
            }
            : undefined;
        const publishParams: PublishParams | undefined = !isCommitOnly && showPublish
            ? {
                versionToPublish,
                isPreRelease,
                releaseType,
            }
            : undefined;
        const shareParams: ShareParams | undefined = !isCommitOnly && showShare
            ? {
                shareType,
                selectedSpaces,
            }
            : undefined;
        // State is set before the actual save as if there's an error from the save, we exit out of this function.
        setHasSaveBeenAttempted(true);
        await saveProcessTemplate({
            type: "Wizard",
            commitParams,
            publishParams,
            shareParams,
        });
    };
    const pages: TemplateWizardPageProps[] = [];
    if (showCommit) {
        pages.push({
            title: "Commit",
            content: () => (<>
                    <CommitSummaryAndDetails onCommitMessageChanged={setCommitMessage} defaultSummary={getDefaultCommitMessage()} commitMessage={commitMessage} commitDetailsAccessibleName={`Commit details for saving the process template process`} commitMessageAccessibleName={`Commit message for saving the process template process`}/>

                    <p>
                        Octopus will commit the changes to the branch <code>{gitRef}</code>
                    </p>
                </>),
            actionAvailable: true,
            additionalActions: [
                <ActionButton key="Commit Only" label="Commit Only" disabled={busy} onClick={async () => {
                        await handleSave(true);
                        closeDialog();
                    }} type={ActionButtonType.Secondary}/>,
            ],
            statusBarLabel: "Commit",
            hideAdditionalActionsOnSinglePage: true,
            singlePageSaveLabel: "Commit",
            singlePageSavingLabel: "Committing...",
        });
    }
    if (showPublish) {
        pages.push({
            title: "Publish version",
            content: () => (<PublishTemplateDialog gitRef={gitRef} defaultBranch={defaultBranch} template={model} templateType={PlatformHubTemplateType.ProcessTemplate} publishedVersions={publishedVersions} versionToPublish={versionToPublish} setVersionToPublish={setVersionToPublish} latestNonPreReleaseVersion={latestNonPreReleaseVersion} isPreRelease={isPreRelease} setIsPreRelease={setIsPreRelease} releaseType={releaseType} setReleaseType={setReleaseType} hasUnsupportedSteps={processTemplateStepsContainAnyUnsupportedActionTypes(model.Steps)}/>),
            actionAvailable: !processTemplateStepsContainAnyUnsupportedActionTypes(model.Steps),
            statusBarLabel: "Publish",
            singlePageSaveLabel: "Publish",
            singlePageSavingLabel: "Publishing...",
        });
    }
    if (showShare) {
        pages.push({
            title: "Share to...",
            content: () => <ShareTemplateDialog spaces={lookupData.spaces} shareType={shareType} selectedSpaces={selectedSpaces} setShareType={setShareType} setSelectedSpaces={setSelectedSpaces} setCanSave={setCanSaveShare}/>,
            actionAvailable: canSaveShare,
            statusBarLabel: "Share",
            singlePageSaveLabel: "Share",
            singlePageSavingLabel: "Sharing...",
        });
    }
    const shouldShowErrors = errors && hasSaveBeenAttempted && !busy;
    return (<CustomDialog open={open} close={noOp} render={(props) => (<TemplateWizardDialogLayout onSaveClick={async () => {
                await handleSave(false);
                return true;
            }} busy={busy} errors={shouldShowErrors ? errors : undefined} savePermission={{ permission: Permission.PlatformHubEdit }} pages={pages}/>)}/>);
}
